import { ChangeEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IDocument } from 'kyc-core/dist/Interfaces'
import useStepFormHook from './useStepFormHook'
import { IStepPropsPeriodique } from '../interfaces/Step'
import {
  ALERT_MESSAGES,
  DOCUMENT_TYPE_ID_103,
  DOCUMENT_TYPE_ID_104,
  DOCUMENT_TYPE_ID_3,
  DOCUMENT_TYPE_ID_4,
  DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS,
  MAX_CARDS,
  MEDIUM_RISK,
  STATUS,
} from '../enums/common'
import { IRevenue } from '../interfaces/Step2'
import useUploadHook from './UseUploadHook'
import { IOption } from '../interfaces/FormFields'
import { ProfessionalSubcategories } from '../components/step2/Options'
import { IStore } from '../interfaces/Store'
import { step2InitialState } from '../store/State/UploadState'
import { IUploadFormState } from '../interfaces/Upload'
import { folderActions } from '../store/slices/Folder'
import useGetJourneyType from './useGetJourneyType'
import { deleteDocument, getDocumentsByTypeIds } from '../services/Kyc'

const Step2FormHook = (props: IStepPropsPeriodique) => {
  const { form, onSubmit, NavButtonsProps } = useStepFormHook(props)
  const folderData = useSelector((state: IStore) => state.folderReducer.folder)
  const { professional_category, no_revenue, csp_niv3 } = folderData
  const { watch } = form
  const { journeyType } = useGetJourneyType()
  let initialDocsState: IUploadFormState[] = [step2InitialState]
  if (folderData.revenue && folderData.revenue.length > 1) {
    initialDocsState = []
    folderData.revenue.forEach(() => {
      initialDocsState.push(step2InitialState)
    })
  }
  const { isPeriodicRemediation } = props
  const isMediumOrLowRisk = useSelector(
    (store: IStore) => store.folderReducer.isMediumOrLowRisk
  )
  const revenuesDocumentUids = folderData?.revenue.flatMap(
    (revenue) => revenue.document_uid
  )

  const dispatch = useDispatch()
  const [state, setState] = useState<IUploadFormState[]>(initialDocsState)
  const [multiFileUpload, setMultiFileUpload] = useState<
    { valid: boolean; index: number; error?: string }[]
  >([])

  const validateFiles = () =>
    state.every((item) => item.status === STATUS.validated)

  useEffect(() => {
    if (multiFileUpload.length > 0) {
      multiFileUpload.forEach((file) => {
        setState((prevState) => {
          const indexPrevState = prevState[file.index]
          const newState = [...prevState]
          newState[file.index] = {
            ...indexPrevState,
            validationButtonError: null,
            status: file.valid ? STATUS.validated : STATUS.loaded,
          }
          return newState
        })
      })
    }
  }, [multiFileUpload])

  const validateMultiFileUpload =
    multiFileUpload.length > 0
      ? multiFileUpload.every((file) => file.valid)
      : false

  const removeAllDocuments = async () => {
    try {
      const documents = await getDocumentsByTypeIds([
        DOCUMENT_TYPE_ID_3,
        DOCUMENT_TYPE_ID_4,
        DOCUMENT_TYPE_ID_103,
        DOCUMENT_TYPE_ID_104,
      ])
      const docsToRemove = documents.filter(
        (doc: IDocument) => !revenuesDocumentUids.includes(doc.documentUid)
      )

      await Promise.all(
        docsToRemove.forEach((document: IDocument) =>
          deleteDocument({ documentUid: document.documentUid })
        )
      )
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }

  useEffect(() => {
    if (folderData?.revenue?.length < 1) {
      removeAllDocuments()
    }
  }, [])

  const validateFile = (index: number) => {
    if (!state[index]) {
      throw new Error('Index not found')
    }
    let valid = true
    // for step2 we only have one file, so we validate on that one
    const [file] = state[index].file
    if (!file) {
      valid = false
      setState((prevState) => {
        const indexPrevState = prevState[index]
        const { fileAlerts } = indexPrevState
        fileAlerts[0] = ALERT_MESSAGES.mandatory
        const newState = [...prevState]
        newState[index] = {
          ...indexPrevState,
          fileAlerts,
          validationButtonError: null,
        }
        return newState
      })
    }

    return valid
  }

  const setFileValidationMessages = () => {
    state.forEach((item, index: number) => {
      if (validateFile(index)) {
        setState((prevState) => {
          const indexPrevState = prevState[index]
          const newState = [...prevState]
          newState[index] = {
            ...indexPrevState,
            validationButtonError: ALERT_MESSAGES.validationButton,
          }
          return newState
        })
      }
    })
  }

  const setMultipleFileMessages = () => {
    multiFileUpload.forEach((file, index) => {
      if (!file.valid) {
        setMultiFileUpload((prevState) => {
          const indexPrevState = prevState[index]
          const newState = [...prevState]
          newState[index] = {
            ...indexPrevState,
            error: ALERT_MESSAGES.validationButton,
          }
          return newState
        })
      }
    })
  }

  const { handleChange, getExistingFiles } = useUploadHook({
    index: 0,
    setState,
    mainState: state,
    validateFile,
  })
  const watchProfessionalCategory =
    watch('professional_category') || professional_category
  const initialProfSubcat: IOption[] = []
  const [professionSubcategories, setProfessionalSubcategories] = useState(
    initialProfSubcat
  )
  const handleProfessionalCategoryChange = (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    const { value } = event.target
    dispatch(folderActions.folderReceived({ csp_niv3: value }))
  }
  const handleProfessionalSubcategoryChange = (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    const { value } = event.target
    dispatch(folderActions.folderReceived({ csp_niv3: value }))
  }
  useEffect(() => {
    if (watchProfessionalCategory > 0 || watchProfessionalCategory === '') {
      setProfessionalSubcategories(
        ProfessionalSubcategories[watchProfessionalCategory] || []
      )
    }
  }, [watchProfessionalCategory])

  const initialRevenue = {
    revenu_type: '1',
    document_type: '',
    revenu_montant: '',
    key: 0,
    document_uid: [],
  }

  const getInitialRevenueTypes = () => {
    if (!folderData.revenue || folderData.revenue.length < 1) {
      return [initialRevenue]
    }
    return folderData.revenue.map((item, index) => ({ ...item, key: index }))
  }
  const [revenueCards, setRevenueCards] = useState<IRevenue[]>(
    getInitialRevenueTypes()
  )
  const removeRevenueCard = async (index: number) => {
    setRevenueCards((prevState) => [
      ...prevState.filter((item, i) => i !== index),
    ])
    setState((prevState) => [...prevState.filter((item, i) => i !== index)])
  }

  const addRevenueCard = async () => {
    const revenueInputs = Object.keys(
      form.getValues()
    ).filter((field: string) => field.startsWith('revenue'))
    const valid = await form.triggerValidation(revenueInputs)
    const filesValid = isMediumOrLowRisk || validateFiles()

    if (!validateMultiFileUpload && multiFileUpload.length > 0) {
      setMultipleFileMessages()
    }

    if (!filesValid || !valid || revenueCards.length === MAX_CARDS) {
      if (!filesValid) {
        setFileValidationMessages()
      }
      return
    }
    setState((prevState) => [...prevState, step2InitialState])
    setRevenueCards((prevState) => {
      return [
        ...revenueCards,
        { ...initialRevenue, key: prevState[prevState.length - 1].key + 1 },
      ]
    })
  }
  const updateRevenueCards = (index: number, value: IRevenue) => {
    setRevenueCards((prevState) => {
      const newRevenueCards = [...prevState]
      newRevenueCards[index] = value
      return newRevenueCards
    })
    setState((prevState) => {
      const indexPrevState = prevState[index]
      const newState = [...prevState]
      newState[index] = {
        ...indexPrevState,
        docValue: value.document_type,
      }
      return newState
    })
  }
  const [noRevenueChecked, setNoRevenueChecked] = useState(
    no_revenue === '1' || true
  )

  const watchNoRevenueRadioField = watch('no_revenue')
  useEffect(() => {
    if (watchNoRevenueRadioField === '1') {
      return setNoRevenueChecked(true)
    }
    if (watchNoRevenueRadioField === '0') {
      return setNoRevenueChecked(false)
    }
  }, [watchNoRevenueRadioField])

  const onRadioChange = (event: any) => {
    setRevenueCards([initialRevenue])
    setState((prevState) => {
      const indexPrevState = prevState[0]
      const newState = []
      newState[0] = {
        ...indexPrevState,
        docValue: 'on',
      }
      return newState
    })
    dispatch(folderActions.removeRevenues())
    // if mediumRisk we don't need to handle documents state changes
    if (isMediumOrLowRisk) {
      return
    }

    handleChange(event, isPeriodicRemediation ? false : !noRevenueChecked)
  }
  const [disableCheckbox, setDisableCheckbox] = useState(false)
  useEffect(() => {
    const isOneValid = state.some((item) => item.status === STATUS.validated)
    if (!disableCheckbox) {
      setDisableCheckbox(isOneValid)
    }
  }, [state, disableCheckbox])

  const handleNextStep = async (event: any) => {
    // if mediumRisk no file is uploaded and the validation is not needed
    if (isMediumOrLowRisk && !isPeriodicRemediation) {
      return
    }
    if (watchNoRevenueRadioField === '1' && !isPeriodicRemediation) {
      return
    }
    if (journeyType === MEDIUM_RISK && isPeriodicRemediation) {
      return
    }

    let validateUploadFiles = false

    if (state.length > 0 && multiFileUpload.length > 0) {
      validateUploadFiles = validateMultiFileUpload && validateFiles()
    }

    if (state.length > 0 && multiFileUpload.length === 0) {
      validateUploadFiles = validateFiles()
    }

    if (state.length === 0 && multiFileUpload.length > 0) {
      validateUploadFiles = validateMultiFileUpload
    }

    if (validateUploadFiles) {
      return
    }
    setMultipleFileMessages()

    event.preventDefault()
    setFileValidationMessages()
    await form.triggerValidation()
  }

  const submitForm = (values: Record<string, any>) => {
    let data = { ...values }

    if (values.revenue) {
      data = {
        ...data,
        revenue: values.revenue
          .map((item: IRevenue, idx: number) => {
            return { ...item, document_uid: revenueCards[idx].document_uid }
          })
          .filter((item: IRevenue) => item !== null),
      }
    }

    onSubmit(data)
  }

  useEffect(() => {
    if (
      !isMediumOrLowRisk &&
      folderData.no_revenue === '1' &&
      !isPeriodicRemediation
    ) {
      setState((prevState) => {
        const indexPrevState = prevState[0]
        const newState = []
        newState[0] = {
          ...indexPrevState,
          docValue: 'on',
        }
        return newState
      })
      getExistingFiles(DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[1].on, true)
    }
    // eslint-disable-next-line
  }, [folderData.no_revenue, isMediumOrLowRisk])

  return {
    state,
    setState,
    submitForm,
    NavButtonsProps,
    form,
    handleNextStep,
    validateFile,
    onRadioChange,
    updateRevenueCards,
    addRevenueCard,
    removeRevenueCard,
    csp_niv3,
    folderData,
    revenueCards,
    noRevenueChecked,
    disableCheckbox,
    professionSubcategories,
    isMediumOrLowRisk,
    handleProfessionalCategoryChange,
    handleProfessionalSubcategoryChange,
    multiFileUpload,
    setMultiFileUpload,
  }
}

export default Step2FormHook
